import React, { useCallback, useImperativeHandle, useMemo } from 'react';
import {
  FileUpload,
  type FileUploadSelectEvent,
  type ItemTemplateOptions,
  type FileUploadHeaderTemplateOptions,
  type FileUploadHandlerEvent,
} from 'primereact/fileupload';
import Cancel from '@mui/icons-material/CancelOutlined';
import CheckCircle from '@mui/icons-material/CheckCircleOutline';
import uploadIcon from '../../../../assets/ic_upload_file.png';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { type components } from '../../../../endpoint/Schema';
import { Icons } from '../../../../assets/icons';

interface IFile {
  name: string;
  objectURL: string;
  size: number;
}

interface ResponseInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error: unknown;
  response: Response;
}

export interface IFileUploadInstance {
  isFileSelected: () => boolean;
  save: () => Promise<ResponseInterface>;
}

export interface IFileUploadProps {
  appFontsType: components['schemas']['AppFontType'];
  disabled?: boolean;
}

const FontUpload = React.forwardRef<IFileUploadInstance, IFileUploadProps>(
  ({ appFontsType, disabled }, ref) => {
    const appFonts = useTenantStore((state) => state.appFonts);
    const tenantId = useTenantStore(selectTenantId);
    const [completed, setCompleted] = React.useState(false);
    const [totalSize, setTotalSize] = React.useState(0);
    const [selectedFile, setSelectedFile] = React.useState<File>();
    const [pending, setPending] = React.useState(false);
    const fileUploadRef = React.useRef<FileUpload>(null);
    const currentFontFile = useMemo(
      () => appFonts?.find((a) => a.name === appFontsType)?.url,
      [appFonts, appFontsType],
    );

    const handleSave = useCallback(async () => {
      if (!selectedFile) return;
      setPending(true);

      try {
        const res = await OpenAPI.POST('/api/v2/admin/tenant/font', {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            // @ts-expect-error
            File: selectedFile,
            AppFontsType: appFontsType,
          },
          bodySerializer: (body) => {
            if (!body) return body;

            const formData = new FormData();
            if (body.File) formData.set('file', body.File);
            if (body.AppFontsType)
              formData.set('appFontsType', body.AppFontsType);
            return formData;
          },
        });

        setPending(false);
        setCompleted(true);
        return res;
      } catch (err) {
        setPending(false);
        throw err;
      }
    }, [appFontsType, selectedFile, tenantId]);

    useImperativeHandle(
      ref,
      () => ({
        isFileSelected: () => !!selectedFile && !completed,
        save: handleSave as () => Promise<ResponseInterface>,
      }),
      [completed, handleSave, selectedFile],
    );

    const myUploader = (event: FileUploadHandlerEvent) => {
      setSelectedFile(event.files[0]);
      event.options.clear();
    };

    const onTemplateSelect = (e: FileUploadSelectEvent) => {
      setCompleted(false);
      setSelectedFile(e.files[0]);
      let _totalSize = totalSize;
      const files = e.files;

      files.forEach((file) => {
        _totalSize += file.size || 0;
      });

      setTotalSize(_totalSize);
    };

    const onTemplateRemove = (file: IFile, callback: () => void) => {
      setTotalSize(totalSize - file.size);
      callback();
      setSelectedFile(undefined);
      setCompleted(false);
    };

    const onTemplateClear = () => {
      setTotalSize(0);
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
      return (
        <div className="flex flex-row mb-4 align-items-center">
          {options.chooseButton}
        </div>
      );
    };

    const itemTemplate = (file: object, props: ItemTemplateOptions) => {
      const fileName = (file as IFile).name;

      return (
        <div className="flex flex-col border rounded-2xl bg-whiteSmoke px-m16 py-m20">
          <div className="flex justify-between items-center">
            <div className="flex flex-1 items-center text-stormGray">
              <img
                loading="lazy"
                src={Icons.ic_png_file}
                className="aspect-1 w-[3rem] mr-1"
              />
              {fileName.length > 20
                ? fileName.substring(0, 18) + '...'
                : fileName}
            </div>

            <button
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                onTemplateRemove(file as IFile, props.onRemove);
              }}
              // className="flex justify-center items-center border border-stormGray p-0.5 rounded-[2rem]"
            >
              {completed ? (
                <CheckCircle
                  className="text-success"
                  sx={{ fontSize: '2rem' }}
                />
              ) : (
                <Cancel className="text-stormGray" sx={{ fontSize: '2rem' }} />
              )}
            </button>
          </div>
        </div>
      );
    };

    const emptyTemplate = () => {
      return (
        <div className="flex flex-col items-center px-5 py-3.5 mt-5 text-sm leading-5 text-zinc-500">
          <img loading="lazy" src={uploadIcon} className="aspect-1 w-[60px]" />
          <div className="mt-11 text-base font-bold leading-6">
            drag and drop.
          </div>
          <div className="mt-2">Maximum file size is 100KB.</div>
          <div className="mt-12">File supported: ttf</div>
        </div>
      );
    };

    return (
      <div className="flex flex-col w-full text-base leading-6 text-black rounded-3xl">
        <FileUpload
          ref={fileUploadRef}
          name="icon[]"
          url="/api/upload"
          accept=".ttf,application/x-font-ttf"
          maxFileSize={100000000}
          onSelect={onTemplateSelect}
          onError={onTemplateClear}
          onClear={onTemplateClear}
          headerTemplate={headerTemplate}
          itemTemplate={itemTemplate}
          emptyTemplate={emptyTemplate}
          chooseOptions={{
            label: 'Click to upload',
            icon: ' ',
            className: '',
          }}
          customUpload={true}
          uploadHandler={myUploader}
          disabled={pending || disabled}
          contentClassName=" rounded-2xl border border-dashed border-neutral-400 px-0 py-0"
        />

        {currentFontFile && (
          <div className="flex bg-white border rounded-2xl border-neutral-300 mt-4 p-12 justify-center items-center">
            <a
              href={currentFontFile}
              target="_blanck"
              className="object-contain"
            >
              Download font file
            </a>
          </div>
        )}
      </div>
    );
  },
);

FontUpload.displayName = 'FontUpload';

export default FontUpload;
